import { useEffect, useMemo, useState, useCallback } from "react"
import { supabase } from "../lib/supabaseClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

// Vite + SVGR import
import OverlaySVG from "@/assets/svg/LabyrinthSelect/DicedScarfLabyrinth.svg?react"
export default function ScarfMap() {
  const keys = useMemo(() => Array.from({ length: 18 }, (_, i) => `q${i + 1}`), [])
  const [dataByKey, setDataByKey] = useState({})
  const [hoveredKey, setHoveredKey] = useState(null)
  const [selectedKey, setSelectedKey] = useState(keys[0])

  // Fetch one row (example: latest). Swap for your own filter.
  useEffect(() => {
    let alive = true
    ;(async () => {
      const { data, error } = await supabase
        .from("Labyrinth Scarf")        // <-- your table
        .select(keys.join(","))        // fetch q1..q18 only
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!alive) return
      if (error) {
        console.error(error)
        return
      }
      // data is one row: { q1: "...", q2: "...", ... }
      setDataByKey(data ?? {})
    })()

    return () => { alive = false }
  }, [keys])

  const activeKey = hoveredKey ?? selectedKey
  const activeText = dataByKey?.[selectedKey] ?? ""

  const move = useCallback((delta) => {
    setSelectedKey((curr) => {
      const idx = keys.indexOf(curr)
      const next = (idx + delta + keys.length) % keys.length
      return keys[next]
    })
  }, [keys])

  const handlePointerMove = (e) => {
    const key = e.target?.dataset?.key
    setHoveredKey(keys.includes(key) ? key : null)
  }

  const handlePointerDown = (e) => {
    const key = e.target?.dataset?.key
    if (keys.includes(key)) setSelectedKey(key)
  }

  return (
    <div className="my-36">
      {/* centered container */}
      <div className="mx-auto w-full max-h-6xl px-6 md:px-12 py-12">
        {/* two columns, centered as a group */}
        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,720px)_minmax(0,560px)] items-center justify-center gap-12">
          {/* LEFT: image + overlay */}
          <div className="relative bg-black rounded-3xl aspect-square w-full max-w-[720px] select-none border-2 border-[#ddd]/100 justify-self-center">
            <img
              src="images/Gianni_scarf (18).png"
              alt="Scarf"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover rounded-none p-12"
            />
  
            <OverlaySVG
              className="absolute inset-0 h-full w-full rounded-none p-12"
              onPointerMove={handlePointerMove}
              onPointerLeave={() => setHoveredKey(null)}
              onPointerDown={handlePointerDown}
            />
  
            {activeKey && (
              <div className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                {activeKey.toUpperCase()}
              </div>
            )}
          </div>
  
          {/* RIGHT: card */}
          <div className="space-y-3 justify-self-center w-full max-w-[560px]">
            <Card className="sticky top-6 bg-white rounded-3xl p-8">
              <CardHeader>
                <CardTitle className="font-sans font-medium text-sm">
                  {selectedKey.toUpperCase()}
                </CardTitle>
              </CardHeader>
  
              <CardContent className="whitespace-pre-wrap text-3xl leading-relaxed text-muted-foreground text-blacK font-kalice font-medium">
                {activeText || "No text for this section yet."}
              </CardContent>
  
              <div className="flex gap-2 my-8 items-center justify-start px-4">
                <Button
                  variant="secondary"
                  className="w-full px-12 bg-transparent border-black hover:border-black border-2 hover:bg-black text-black hover:text-white rounded-none font-sans tracking-wide text-sm"
                  onClick={() => move(-1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  className="w-full px-12 bg-black text-white hover:bg-black/80 rounded-none font-sans tracking-wide text-sm"
                  onClick={() => move(+1)}
                >
                  Next
                </Button>
              </div>
            </Card>
          </div>
  
          <SVGOverlayStyles activeKey={activeKey} />
        </div>
      </div>
    </div>
  )
  

}

/**
 * Styles that make the overlay shapes clickable but visually subtle.
 * We highlight hovered/selected by changing fill opacity + stroke.
 */
function SVGOverlayStyles({ activeKey }) {
  return (
    <style>{`
      /* Make all regions clickable but visually transparent */
      svg [data-key] {
        fill: rgba(255,255,255,0);
        /* fill: rgba(0,0,0,0.30); */
        /* stroke: rgba(255,255,255,0); */
/*         stroke-width: 38px; */
        cursor: pointer;
        transition: fill 120ms ease, stroke 120ms ease;
      }

      /* Hover effect (when React sets hoveredKey via pointer move, we don't have a CSS :hover target easily
         because we're delegating at the SVG level. So we do highlight via a data attribute set in React
         OR we can rely on :hover as well. */
      svg [data-key]:hover {
        fill: rgba(255,255,255,0.80);
        stroke: rgba(255,255,255,1.25);
      }

      /* Active highlight (selected or hovered) */
      ${activeKey ? `svg [data-key="${activeKey}"] { fill: rgba(255,255,255,0.18); stroke: rgba(255,255,255,0.35); }` : ""}
    `}</style>
  )
}
